import type {
  AttackTechnique,
  BehaviorChain,
  BehaviorChainStage,
  DataFlowTrace,
  DetectedAction,
  Severity,
} from "./types";

interface ChainPattern {
  id: string;
  title: string;
  description: string;
  stages: Array<{
    role: BehaviorChainStage["role"];
    title: string;
    categories?: DetectedAction["category"][];
    actionIds?: RegExp;
    sinkKinds?: DataFlowTrace["sinkKind"][];
    sourceKinds?: DataFlowTrace["sourceKind"][];
  }>;
}

const patterns: ChainPattern[] = [
  {
    id: "credential-exfiltration",
    title: "Credential collection and exfiltration",
    description:
      "Credential or session data is collected and reaches an outbound network sink.",
    stages: [
      {
        role: "collect",
        title: "Collect account or session data",
        categories: ["credential", "minecraft", "collection"],
        sourceKinds: [
          "minecraft-access-token",
          "minecraft-session",
          "launcher-account",
          "browser-credential",
          "discord-token",
        ],
      },
      {
        role: "deliver",
        title: "Send data to a remote destination",
        categories: ["network"],
        sinkKinds: ["http-request", "socket-write", "webhook"],
      },
    ],
  },
  {
    id: "download-execute",
    title: "Download, write, and execute",
    description:
      "Remote content is retrieved, staged locally, and passed to an execution or class-loading sink.",
    stages: [
      {
        role: "deliver",
        title: "Download remote content",
        categories: ["download", "network"],
        sourceKinds: ["remote-content"],
      },
      {
        role: "stage",
        title: "Write or unpack payload",
        categories: ["filesystem"],
        sinkKinds: ["file-write"],
      },
      {
        role: "execute",
        title: "Execute or load payload",
        categories: ["execution"],
        sinkKinds: ["process-execution", "dynamic-class-load"],
      },
    ],
  },
  {
    id: "security-control-tampering-persistence",
    title: "Security control tampering and persistence",
    description:
      "Security controls are modified or bypassed alongside a persistence mechanism.",
    stages: [
      {
        role: "evade",
        title: "Tamper with security controls",
        categories: ["evasion"],
        actionIds: /defender|security|exclude|tamper|signature/i,
      },
      {
        role: "persist",
        title: "Establish persistence",
        categories: ["persistence"],
      },
    ],
  },

  {
    id: "clipboard-exfiltration",
    title: "Clipboard collection and exfiltration",
    description:
      "Clipboard contents are read and later reach an outbound network or webhook sink.",
    stages: [
      {
        role: "collect",
        title: "Read clipboard contents",
        categories: ["collection"],
        actionIds: /clipboard/i,
        sourceKinds: ["clipboard"],
      },
      {
        role: "deliver",
        title: "Send clipboard data remotely",
        categories: ["network"],
        sinkKinds: ["http-request", "socket-write", "webhook"],
      },
    ],
  },
  {
    id: "webhook-credential-exfiltration",
    title: "Credential theft via webhook",
    description:
      "Account or session material is collected and delivered through a webhook-style sink.",
    stages: [
      {
        role: "collect",
        title: "Collect credentials or tokens",
        categories: ["credential", "minecraft", "collection"],
        sourceKinds: [
          "minecraft-access-token",
          "minecraft-session",
          "launcher-account",
          "browser-credential",
          "discord-token",
        ],
      },
      {
        role: "deliver",
        title: "Post to a webhook destination",
        categories: ["network"],
        actionIds: /webhook|discord|telegram|exfil/i,
        sinkKinds: ["webhook", "http-request"],
      },
    ],
  },
  {
    id: "reflective-payload-execution",
    title: "Reflective download and class loading",
    description:
      "Remote or staged bytes are loaded through reflective class-loading APIs after a download or write stage.",
    stages: [
      {
        role: "deliver",
        title: "Obtain remote or staged bytes",
        categories: ["download", "network", "filesystem"],
        sourceKinds: ["remote-content"],
      },
      {
        role: "execute",
        title: "Load classes reflectively",
        categories: ["execution"],
        actionIds: /define-class|url-class-loader|class-loader|reflective/i,
        sinkKinds: ["dynamic-class-load"],
      },
    ],
  },
  {
    id: "collection-archive-exfiltration",
    title: "Collect, package, and exfiltrate data",
    description:
      "Local data is collected, transformed or archived, then sent to a remote destination.",
    stages: [
      {
        role: "collect",
        title: "Collect local data",
        categories: ["collection", "credential", "minecraft"],
      },
      {
        role: "transform",
        title: "Transform or package data",
        actionIds: /archive|zip|json|encrypt|encode|compress/i,
      },
      {
        role: "deliver",
        title: "Exfiltrate packaged data",
        categories: ["network"],
        sinkKinds: ["http-request", "socket-write", "webhook"],
      },
    ],
  },
];

function severityRank(value: Severity) {
  return ["info", "low", "medium", "high", "critical"].indexOf(value);
}

function matchesStage(
  stage: ChainPattern["stages"][number],
  action: DetectedAction,
) {
  return (
    (!stage.categories || stage.categories.includes(action.category)) &&
    (!stage.actionIds ||
      stage.actionIds.test(
        `${action.actionId} ${action.title} ${action.tags.join(" ")}`,
      ))
  );
}

function flowMatchesStage(
  stage: ChainPattern["stages"][number],
  flow: DataFlowTrace,
) {
  return (
    (!stage.sourceKinds || stage.sourceKinds.includes(flow.sourceKind)) &&
    (!stage.sinkKinds || stage.sinkKinds.includes(flow.sinkKind))
  );
}

function unique<T>(items: T[]) {
  return Array.from(new Set(items));
}

export function buildBehaviorChains(
  actions: DetectedAction[],
  flows: DataFlowTrace[],
): BehaviorChain[] {
  const results: BehaviorChain[] = [];

  for (const pattern of patterns) {
    const stageMatches = pattern.stages.map((stage) => {
      const matchingActions = actions.filter((action) =>
        matchesStage(stage, action),
      );
      const matchingFlows = flows.filter((flow) =>
        flowMatchesStage(stage, flow),
      );
      return { stage, matchingActions, matchingFlows };
    });

    if (stageMatches.some((stage) => !stage.matchingActions.length && !stage.matchingFlows.length)) {
      continue;
    }

    const relatedActions = unique(
      stageMatches.flatMap((stage) => stage.matchingActions),
    );
    const relatedFlows = unique(
      stageMatches.flatMap((stage) => stage.matchingFlows),
    );
    const reachable =
      relatedFlows.some((flow) => flow.reachable) ||
      relatedActions.some((action) => action.reachable);
    const ordering =
      relatedFlows.some((flow) => flow.ordering === "proven")
        ? "proven"
        : relatedFlows.length
          ? "partial"
          : "correlated";
    const baseSeverity = relatedActions.reduce<Severity>(
      (highest, action) =>
        severityRank(action.severity) > severityRank(highest)
          ? action.severity
          : highest,
      "info",
    );
    const severity: Severity =
      (pattern.id === "credential-exfiltration" || pattern.id === "webhook-credential-exfiltration") && ordering === "proven"
        ? "critical"
        : baseSeverity === "critical" || baseSeverity === "high"
          ? baseSeverity
          : ordering === "proven"
            ? "high"
            : "medium";
    const confidence =
      ordering === "proven" && reachable
        ? "high"
        : relatedFlows.length || reachable
          ? "medium"
          : "low";

    const stages: BehaviorChainStage[] = stageMatches.map(
      ({ stage, matchingActions, matchingFlows }, index) => {
        const action = matchingActions[0];
        const flow = matchingFlows[0];
        return {
          index,
          role: stage.role,
          title: stage.title,
          detail:
            action?.description ??
            flow?.description ??
            "Correlated static evidence",
          entry: action?.entry ?? flow?.entry ?? "multiple entries",
          method: action?.method ?? flow?.method,
          evidence: unique([
            ...(action?.evidence ?? []),
            ...(flow?.steps.map((step) => step.detail) ?? []),
          ]).slice(0, 8),
        };
      },
    );

    const techniques = unique(
      relatedActions.flatMap((action) => action.techniques ?? []),
    ).filter(
      (technique, index, items) =>
        items.findIndex((item) => item.id === technique.id) === index,
    ) as AttackTechnique[];

    results.push({
      id: `chain-${pattern.id}`,
      title: pattern.title,
      description: pattern.description,
      severity,
      confidence,
      ordering,
      reachable,
      entryPoint:
        relatedFlows.find((flow) => flow.entryPoint)?.entryPoint ??
        relatedActions.find((action) => action.entryPoint)?.entryPoint,
      stages,
      destinations: unique([
        ...relatedFlows.flatMap((flow) => flow.destinations),
        ...relatedActions.flatMap((action) => action.destinations ?? []),
      ]),
      dataTypes: unique([
        ...relatedFlows.map((flow) => flow.sourceLabel),
        ...relatedActions.flatMap((action) => action.dataTypes ?? []),
      ]),
      techniques,
      relatedActionIds: relatedActions.map((action) => action.id),
      relatedFlowIds: relatedFlows.map((flow) => flow.id),
    });
  }

  return results.sort(
    (left, right) =>
      severityRank(right.severity) - severityRank(left.severity) ||
      Number(right.ordering === "proven") - Number(left.ordering === "proven"),
  );
}
